import { useCallback, useState, type ReactElement } from 'react';
import type { MediaDto } from '@gr/core/content/media/dto';
import { MediaKind } from '@gr/core/content/media/formats';
import { BlogPermission } from '@gr/core/accounts/workspaces/permissions';
import { useConfig, useServices, useT } from '../../../app/di';
import { arrayPaging, usePagedResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button, ShowMoreButton } from '../../../ui-kit/Button/Button';
import { Dropzone } from '../../../ui-kit/Dropzone/Dropzone';
import { EmptyState } from '../../../ui-kit/EmptyState/EmptyState';
import { BareInput, BareSelect, Checkbox } from '../../../ui-kit/Field/Field';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { useBlog } from '../../accounts/workspaces';
import { MediaGrid } from './MediaGrid/MediaGrid';
import { MediaTile } from './MediaTile/MediaTile';
import { UPLOAD_ACCEPT } from './model/uploads';
import { UploadList } from './UploadList/UploadList';
import { useUploads } from './use-uploads';

const MEDIA_PAGING = arrayPaging<MediaDto>();

// MED-UC-1, MED-UC-2
export function MediaLibraryPage(): ReactElement {
  const { api } = useServices();
  const { t } = useT();
  const { blog, can } = useBlog();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<'' | MediaKind>('');
  const [unattached, setUnattached] = useState(false);
  const { ui } = useConfig();
  const list = usePagedResource(
    (page) => api.media.list(blog.id, { q, kind: kind || undefined, unattached: unattached || undefined, ...page }),
    [api, blog.id, q, kind, unattached],
    MEDIA_PAGING,
    ui.lists.pageSize,
  );
  const onUploaded = useCallback(() => void list.reload(), [list.reload]);
  const uploads = useUploads(blog.id, onUploaded);
  const filtered = Boolean(q || kind || unattached);

  return (
    <div className="u-stack-lg">
      <PageHeader title={t('media.library.title')} subtitle={t('media.library.subtitle')} />
      {can(BlogPermission.EditContent) ? <Dropzone accept={UPLOAD_ACCEPT} label={t('media.library.upload')} hint={t('media.library.uploadHint')} onFiles={(files) => void uploads.upload(files)} /> : null}
      <UploadList items={uploads.items} onClear={uploads.clear} />
      <div className="u-row">
        <div className="u-grow u-measure-sm">
          <BareInput label={t('media.library.search')} placeholder={t('media.library.search')} type="search" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <BareSelect
          label={t('media.library.kind')}
          value={kind}
          onChange={(e) => setKind(e.target.value as '' | MediaKind)}
          options={[
            { value: '', label: t('media.library.kinds.all') },
            { value: MediaKind.Image, label: t('media.library.kinds.image') },
            { value: MediaKind.Video, label: t('media.library.kinds.video') },
          ]}
        />
        <Checkbox label={t('media.library.unattached')} checked={unattached} onChange={(e) => setUnattached(e.target.checked)} />
      </div>
      {list.loading && !list.data ? (
        <Loading label={t('common.loading')} />
      ) : list.error ? (
        <Alert tone="danger" title={t('common.loadFailed')}>
          <Button size="sm" onClick={() => void list.reload()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : !list.data || list.data.length === 0 ? (
        <EmptyState title={filtered ? t('media.library.emptySearch') : t('media.library.empty')} text={filtered ? undefined : t('media.library.emptyText')} />
      ) : (
        <MediaGrid label={t('media.library.title')} items={list.data} renderItem={(media) => <MediaTile media={media} />} />
      )}
      <ShowMoreButton visible={list.hasMore && !list.error} loading={list.loadingMore} onClick={() => void list.loadMore()}>
        {t('common.showMore')}
      </ShowMoreButton>
    </div>
  );
}
